export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';
export const DEFAULT_TIME_FORMAT = 'HH:mm';
export const MS_IN_SECOND = 1000;
export const MS_IN_MINUTE = MS_IN_SECOND * 60;
export const MS_IN_HOUR = MS_IN_MINUTE * 60;
export const MS_IN_DAY = MS_IN_HOUR * 24;
export const MS_IN_WEEK = MS_IN_DAY * 7;
export const MS_IN_YEAR = MS_IN_DAY * 365;
export const ARR_HOURS_IN_A_DAY = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
export const ARR_5_MINUTES_INTERVAL = Array.from({ length: Math.floor(60 / 5) }).map((_, i) => i * 5);
function zeroPad(num) {
    return String(num).padStart(2, '0');
}
export function convertDateToTimezone(from, timezoneOrOffset) {
    if (typeof timezoneOrOffset === 'number') {
        const target = new Date(from);
        const inherentOffset = target.getTimezoneOffset();
        target.setMinutes(target.getMinutes() + inherentOffset + timezoneOrOffset);
        return target;
    }
    return new Date(new Date(from).toLocaleString('en', { timeZone: timezoneOrOffset }));
}
export function dateToString(dateString, timezoneOrOffset) {
    let date = new Date(dateString);
    if (timezoneOrOffset) {
        date = convertDateToTimezone(dateString, timezoneOrOffset);
    }
    return `${date.getFullYear()}-${zeroPad(date.getMonth() + 1)}-${zeroPad(date.getDate())} ${zeroPad(date.getHours())}:${zeroPad(date.getMinutes())}`;
}
export function roundedToTheHalf(date) {
    let d = new Date();
    if (date) {
        d = new Date(date);
    }
    const minutes = d.getMinutes();
    d.setMilliseconds(0);
    d.setSeconds(0);
    if (minutes < 15) {
        d.setMinutes(0);
    }
    else if (minutes < 45) {
        d.setMinutes(30);
    }
    else {
        d.setHours(d.getHours() + 1);
        d.setMinutes(0);
    }
    return d;
}
export function extractDateFromISO(isoDate) {
    if (!isoDate) {
        return '';
    }
    return isoDate.slice(0, 10);
}
export function extractToLocal(date) {
    return FlexibleDate.buildIsoDate(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes());
}
export function extractTimeFromISO(isoDate) {
    if (!isoDate) {
        return '';
    }
    const match = timeFormatRegExp.exec(isoDate);
    if (!match) {
        return '';
    }
    return `${match[1]}:${match[2]}`;
}
export function fromLocalTimeToTimezone(localTime, timezone) {
    const now = new Date();
    const delta = now.getTime() - convertDateToTimezone(now, timezone).getTime();
    return new Date(new Date(localTime).getTime() + delta);
}
export function fromTimezoneToLocal(timezoneTime, timezone) {
    const now = new Date();
    const delta = now.getTime() - convertDateToTimezone(now, timezone).getTime();
    return new Date(new Date(timezoneTime).getTime() - delta);
}
export function computeDelta(start, end, compute) {
    let remainingInMs = end - start;
    let years = 0;
    let weeks = 0;
    let days = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    if (compute.includes('years')) {
        years = MS_IN_YEAR <= remainingInMs ? Math.floor(remainingInMs / MS_IN_YEAR) : 0;
        remainingInMs -= MS_IN_YEAR * years;
    }
    if (compute.includes('weeks')) {
        weeks = MS_IN_WEEK <= remainingInMs ? Math.floor(remainingInMs / MS_IN_WEEK) : 0;
        remainingInMs -= MS_IN_WEEK * weeks;
    }
    if (compute.includes('days')) {
        days = MS_IN_DAY <= remainingInMs ? Math.floor(remainingInMs / MS_IN_DAY) : 0;
        remainingInMs -= MS_IN_DAY * days;
    }
    if (compute.includes('hours')) {
        hours = MS_IN_HOUR <= remainingInMs ? Math.floor(remainingInMs / MS_IN_HOUR) : 0;
        remainingInMs -= hours * MS_IN_HOUR;
    }
    if (compute.includes('minutes')) {
        minutes = MS_IN_MINUTE <= remainingInMs ? Math.floor(remainingInMs / MS_IN_MINUTE) : 0;
        remainingInMs -= minutes * MS_IN_MINUTE;
    }
    if (compute.includes('seconds')) {
        seconds = MS_IN_SECOND <= remainingInMs ? Math.floor(remainingInMs / MS_IN_SECOND) : 0;
    }
    return {
        years,
        weeks,
        days,
        hours,
        minutes,
        seconds
    };
}
export const dateFormatRegExp = /(\d{4})-(\d{2})-(\d{2})/;
export const timeFormatRegExp = /(\d{1,2}):(\d{2})/;
export var FlexibleDateFormat;
(function (FlexibleDateFormat) {
    // only date YYYY-MM-DD
    FlexibleDateFormat[FlexibleDateFormat["short"] = 0] = "short";
    // only date without day YYYY-MM
    FlexibleDateFormat[FlexibleDateFormat["shortWithoutDay"] = 1] = "shortWithoutDay";
    // full iso YYYY-MM-DDTHH:mm-<TZ>
    FlexibleDateFormat[FlexibleDateFormat["iso"] = 2] = "iso";
    // full iso YYYY-MM-DDTHH:mm
    FlexibleDateFormat[FlexibleDateFormat["isoWithoutTimezone"] = 3] = "isoWithoutTimezone";
    // full iso YYYY-MM-DD HH:mm
    FlexibleDateFormat[FlexibleDateFormat["human"] = 4] = "human";
})(FlexibleDateFormat || (FlexibleDateFormat = {}));
export class FlexibleDate {
    constructor(date, timezone) {
        this.date = new Date(date);
        if (timezone) {
            this.setTimezone(timezone);
        }
    }
    static buildIsoDate(year, month, day, hours, minutes) {
        return `${year}-${zeroPad(month)}-${zeroPad(day)}T${zeroPad(hours)}:${zeroPad(minutes)}`;
    }
    static fromUtc(iso, timezone) {
        return new FlexibleDate(convertDateToTimezone(iso, timezone), timezone);
    }
    setTimezone(timezone) {
        this.timezone = timezone;
        this.date = this.convertToTimezone(this.timezone);
    }
    toLocalString(format, option = {}) {
        switch (format) {
            case FlexibleDateFormat.short:
                return `${this.date.getFullYear()}-${String(this.date.getMonth() + 1).padStart(2, '0')}-${String(this.date.getDate()).padStart(2, '0')}`;
            case FlexibleDateFormat.shortWithoutDay:
                return `${this.date.getFullYear()}-${String(this.date.getMonth() + 1).padStart(2, '0')}`;
            case FlexibleDateFormat.isoWithoutTimezone:
                return FlexibleDate.buildIsoDate(this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate(), this.date.getHours(), !option.omitMinutes ? this.date.getMinutes() : 0);
            case FlexibleDateFormat.iso:
                return this.date.toISOString();
            case FlexibleDateFormat.human:
                return `${this.toLocalString(FlexibleDateFormat.short)} ${this.extractTime()}`;
        }
    }
    toMs() {
        return this.date.getTime();
    }
    getDate() {
        return new Date(this.date);
    }
    getFullYear() {
        return this.date.getFullYear();
    }
    /**
     * returns 14:45 or 02:01
     */
    extractTime() {
        return `${String(this.date.getHours()).padStart(2, '0')}:${String(this.date.getMinutes()).padStart(2, '0')}`;
    }
    convertToTimezone(timezone) {
        return convertDateToTimezone(this.date, timezone);
    }
    addYears(years) {
        this.date.setFullYear(this.date.getFullYear() + years);
        return this;
    }
    addMonths(months) {
        this.date.setMonth(this.date.getMonth() + months);
        return this;
    }
    addDays(days) {
        this.date.setDate(this.date.getDate() + days);
        return this;
    }
    addHours(hours) {
        this.date.setHours(this.date.getHours() + hours);
        return this;
    }
    addMinutes(minutes) {
        this.date.setMinutes(this.date.getMinutes() + minutes);
        return this;
    }
    isBefore(date) {
        if (date.isBefore) {
            return this.date < date.getDate();
        }
        const d = date;
        const other = new Date(d);
        return this.date < other;
    }
    isAfter(date) {
        if (date.isBefore) {
            return date.getDate() < this.date;
        }
        const d = date;
        const other = new Date(d);
        return other < this.date;
    }
    clone() {
        return new FlexibleDate(this.date, this.timezone);
    }
}
